namespace Illy.SafeStack.Client

[<AutoOpen>]
module Shared =
    open Fable.Core
    open Elmish
    open Fetch
    open Thoth.Json
    open Fable.DateFunctions.CommonHelpers

    type FetchError = 
        | DecodeError of string
        | ResponseFailed of statusCode: int * rawResponse: string
        | PromiseError of exn

    let inline formatDate f (d: System.DateTime) = d.Format f
    let illyDate = formatDate "dd/MMM/yyyy"
    let illyDateTime = formatDate "dd/MMM/yyyy HH:mm"

    let [<Emit("('ActiveXObject' in window)")>] isBrowserIE () : bool =
        jsNative
    
    module private CustomDecoders = 
        let inline private isString (o: JsonValue) : bool = o :? string
        let inline private asString (o: JsonValue): string = unbox o
                
        ///Override the default behavior of Thoth which is to convert dates to UTC
        let datetime: Decoder<System.DateTime> =
            fun path value ->
                if isString value then
                    match System.DateTime.TryParse (asString value) with
                    | true, x -> x.ToUniversalTime().ToLocalTime() |> Ok
                    | _ -> (path, BadPrimitive("a datetime", value)) |> Error
                else
                    (path, BadPrimitive("a datetime", value)) |> Error

    let extraCoders = 
        Extra.empty
        |> Extra.withDecimal
        |> Extra.withCustom Encode.datetime CustomDecoders.datetime

    let inline fetchAndDecode<'a> url props =
        promise {
            let! response = GlobalFetch.fetch(RequestInfo.Url url, requestProps props)
            
            if response.Ok then 
                let! txt = response.text()
                let res = Thoth.Json.Decode.Auto.fromString<'a>(txt, extra=extraCoders)

                match res with
                | Ok x -> 
                    return Result.Ok x
                | Result.Error err -> 
                    return Result.Error (DecodeError err)
            else 
                let! txt = response.text()
                return Result.Error (ResponseFailed (response.Status, txt))
        }

    let setCSRF csrf = HttpRequestHeaders.Custom("csrf", csrf)
    
    let getCSRFFromHiddenField() =
        let csrf = Browser.Dom.document.querySelector("input[name=csrf]")
        let attr = csrf.attributes.getNamedItem "value"
        setCSRF attr.value

    let debounce timeoutMs =
        let mutable timeoutActive: int option = None 
        let mutable store = Unchecked.defaultof<'msg * 'msgValue>
    
        fun send msg msgValue ->
            store <- msg, msgValue
            if timeoutActive.IsSome then JS.clearTimeout timeoutActive.Value
        
            timeoutActive <- 
                JS.setTimeout (fun () ->
                    send store
                    timeoutActive <- None) timeoutMs
                |> Some
    
    let debounceDispatch timeoutMs = 
        let debouncer = debounce timeoutMs
        fun dispatch (msg: 'msg) -> 
            debouncer 
                (fun (m, _) -> dispatch m)
                msg
                ()

    let setTimeout (milliseconds: int) successPayload onSuccess onError = 
        let continueAfterTime input= 
            async {
                do! Async.Sleep(milliseconds)
                return input
            }
        Cmd.OfAsync.either continueAfterTime successPayload onSuccess onError

module Moment =
// F# implementation for the javascript date library moment.js
    open System

    type private Direction =
        | Past
        | Future

    let private roundFloatToInt (x: float) =
        x
        |> System.Math.Round
        |> Convert.ToInt32

    let fromNow (d: DateTime) =
        let timespanBeforeNow = DateTime.UtcNow - d
        let timespan, direction =
            if timespanBeforeNow.TotalMilliseconds < 0.
                then timespanBeforeNow.Negate(), Future
                else timespanBeforeNow, Past
        
        let magnitude =
            match timespan with
            | x when 0. <= x.TotalSeconds && x.TotalSeconds < 45. -> "a few seconds"
            | x when 45. <= x.TotalSeconds && x.TotalSeconds < 90. -> "a minute"
            | x when 90. <= x.TotalSeconds && x.TotalMinutes < 45. -> sprintf "%d minutes" <| roundFloatToInt x.TotalMinutes
            | x when 45. <= x.TotalMinutes && x.TotalMinutes < 90. -> "an hour"
            | x when 90. <= x.TotalMinutes && x.TotalHours < 22. -> sprintf "%d hours" <| roundFloatToInt x.TotalHours
            | x when 22. <= x.TotalHours && x.TotalHours < 36. -> "a day"
            | x when 36. <= x.TotalHours && x.TotalDays < 26. -> sprintf "%d days" <| roundFloatToInt x.TotalDays
            | x when 26. <= x.TotalDays && x.TotalDays < 45. -> "a month"
            | x when 45. <= x.TotalDays && x.TotalDays < 320. -> sprintf "%d months" <| roundFloatToInt (x.TotalDays / 30.)
            | x when 320. <= x.TotalDays && x.TotalDays < 548. -> "a year"
            | x when 548. <= x.TotalDays -> sprintf "%d years" <| roundFloatToInt (x.TotalDays / 364.)
            | x -> failwith <| sprintf "case not implemented %A" x
        
        if direction = Past then
            sprintf "%s ago" magnitude
        else
            sprintf "in %s" magnitude

module HTMLElementPosition =
    open Fable.React.Props

    type HTMLElementId = HTMLElementId of string

    module HTMLElementId = 
        let unwrap = function HTMLElementId str -> str
        let getElementById = unwrap >> Browser.Dom.document.getElementById >> fun x -> if isNull x then None else Some x
    
    type TopCoords = 
        {
            Bottom: float
            Left: float
        }

    type BottomCoords = 
        {
            Top: float
            Left: float
        }
    
    [<RequireQualifiedAccess>]
    type Position = 
        | Top of TopCoords
        | Bottom of BottomCoords
    
    module Position = 
        let toCSSClass = 
            function 
            | Position.Top _ -> "position-top"
            | Position.Bottom _ -> "position-bottom"

    type PopupMetaData = 
        {
            Width: float
            Position: Position
        }

    let private calculatePositionInRelationToViewport maxHeight (boundaries: Browser.Types.ClientRect) = 
        let viewportHeight = Browser.Dom.window.innerHeight

        let left = boundaries.left
        
        if viewportHeight < (boundaries.bottom + maxHeight) then
            { Bottom = viewportHeight - boundaries.top; Left = left } |> Position.Top
        else
            { Top = boundaries.bottom; Left = boundaries.left } |> Position.Bottom
    
    module PopupMetaData = 
        let create maxHeight (elementToPinTo: Browser.Types.HTMLElement) = 
            {
                Width = elementToPinTo.offsetWidth
                Position = calculatePositionInRelationToViewport maxHeight (elementToPinTo.getBoundingClientRect())
            }

    let calculatePopupPosition maxHeight elementToPinTo =
        elementToPinTo 
        |> HTMLElementId.getElementById 
        |> Option.map (PopupMetaData.create maxHeight)

    let generatePositionStyle position : CSSProp list= 
        [
            yield Position PositionOptions.Fixed
            match position with
            | Some (Position.Bottom coords) -> 
                yield Left coords.Left
                yield Top coords.Top
            | Some (Position.Top coords) -> 
                yield Left coords.Left
                yield Bottom coords.Bottom
            | None -> 
                yield Left 0
                yield Top 0
        ]
    
    let generatePositionStyleWithWidth position : CSSProp list= 
        [
            yield! generatePositionStyle (position |> Option.map (fun x -> x.Position))
            match position with 
            | Some dw -> yield Width (sprintf "%dpx" (int dw.Width))
            | None -> ()
        ]

module Comparison = 
    let compareTo x y = 
        if x = y then 0
        else if x < y then -1
        else 1